import apiClient, { type ApiResponse } from '../../../shared/utils/axios';
import type {
  ScreensResult,
  ScreenFilterDef,
  ScreenQueryParams,
  RegisterDeviceResult,
  BulkRegisterResult,
  ScreensHeadersColumn,
  ScreenRow,
} from '@/entities/screen';
import type {
  AllPlatformFirmwareResponse,
  FirmwareBuilds,
  FirmwareBuildsResponse,
} from '@/entities/firmware';

interface ScreenListData {
  headers: ScreensHeadersColumn[];
  rows: ScreenRow[];
  total: number;
}

class ScreenAPI {
  async fetchScreens(params: ScreenQueryParams): Promise<ScreensResult> {
    const {
      filters = {},
      search = '',
      page = 0,
      rowsPerPage = 10,
    } = params || {};

    // Format filters similar to your Redux action
    const formattedSelectedFilters = Object.entries(filters)
      .filter(([key, filter]) => !(key === 'state' && filter.value === 'ALL'))
      .map(([_, filter]) => {
        const { values, value, ...rest } = filter;
        return {
          ...rest,
          value: Array.isArray(value) ? value : [value],
        };
      });

    // Build URL with query parameters
    let url = `/dooh_api/screen/v1/devices/list?limit=${rowsPerPage}&offset=${page * rowsPerPage}&filterType=advertiser`;

    if (search && search.length > 2) {
      url += `&searchText=${encodeURIComponent(search)}`;
    }

    // Use POST with filters in body (matching your original logic)
    const response = await apiClient.post<ScreenListData>(url, {
      filters: formattedSelectedFilters,
      sort: [],
    });

    // Transform response to match expected format
    const data = response?.data;
    return {
      headers: data?.headers || [],
      rows: data?.rows || [],
      totalItems: data?.total || 0,
      page,
      rowsPerPage,
    };
  }

  async fetchScreenFilters(
    filters: any = {},
    filterType: string = ''
  ): Promise<ScreenFilterDef[]> {
    let url = `/dooh_api/screen/v1/devices/filters`;
    if (filterType) {
      url += `?filterType=${filterType}`;
    }
    const response = await apiClient.post<ScreenFilterDef[]>(url, filters);
    return response?.data || [];
  }

  async getScreenById(data: { id: string | null; day?: string }): Promise<any> {
    try {
      let url = `/dooh_api/sdk/v1/device/${data.id}`;

      if (data.day) {
        url += `?day=${data.day}`;
      }

      const response = await apiClient.get(url);

      if (response.success) {
        return response?.data;
      }
    } catch (error) {
      console.error('Error fetching screen by ID:', error);
      throw error;
    }
  }

  async getGeoAddress(lat: string, long: string) {
    try {
      const response = await apiClient.get(
        `/dooh_api/screen/v1/address?latitude=${lat}&longitude=${long}`
      );
      if (response.success) {
        return response?.data;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async setScreenStatus(data: any, deviceId: string): Promise<any> {
    try {
      const response = await apiClient.post(
        `/dooh_api/screen/v1/device/${deviceId}/approve`,
        data
      );
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async registerDevice(
    values: any
  ): Promise<ApiResponse<RegisterDeviceResult>> {
    try {
      const {
        latitude,
        longitude,
        width,
        height,
        city,
        country,
        landmark,
        addressType,
        addressState,
        postcode,
        deviceImage,
        price,
        loopDuration,
        slotTimeInSec,
        sizeHeight,
        sizeWidth,
        screenOnDays,
        screenOnTime,
        touchpointName,
        networkName,
        category,
        placement,
        ...rest
      } = values;

      const response = await apiClient.post<RegisterDeviceResult>(
        '/dooh_api/screen/v1/device/register',
        {
          ...rest,
          price: Number(price),
          loopDuration: Number(loopDuration),
          slotTimeInSec: Number(slotTimeInSec),
          sizeHeight: sizeHeight || undefined,
          sizeWidth: sizeWidth || undefined,
          category,
          placement,
          touchpointName: touchpointName || undefined,
          areaTags: networkName ? [networkName] : undefined,
          geoCoordinates: {
            latitude: String(latitude),
            longitude: String(longitude),
          },
          resolution: {
            width: Number(width),
            height: Number(height),
          },
          address: {
            city,
            country: country || 'India',
            landmark,
            state: addressState,
            type: addressType,
            postcode:
              postcode !== undefined && postcode !== null && postcode !== ''
                ? String(postcode)
                : undefined,
          },
          deviceImage:
            deviceImage?.map((item: any) => {
              const { progress, status, ...rest } = item;
              return rest;
            }) || [],
          screenOnDays: screenOnDays || [],
          screenOnTime: screenOnTime ?? {
            from: { hours: '09', minutes: '00' },
            to: { hours: '21', minutes: '00' },
          },
          approved: true,
        }
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async registerBulkDevices(
    r2FileUrl: string
  ): Promise<ApiResponse<BulkRegisterResult>> {
    const formData = new FormData();
    formData.append('file', r2FileUrl);

    const response = await apiClient.post<BulkRegisterResult>(
      '/dooh_api/screen/v1/device/register/bulk',
      formData,
      {
        transformRequest: [
          (data: FormData, headers: Record<string, string>) => {
            delete headers['Content-Type'];
            return data;
          },
        ],
      }
    );
    return response;
  }

  async editDevice(deviceId: string, values: any): Promise<any> {
    const response = await apiClient.patch(
      `/dooh_api/screen/v1/device/${deviceId}`,
      values
    );
    return response;
  }

  async fetchAllPlatformFirmware(): Promise<AllPlatformFirmwareResponse> {
    try {
      const response = await apiClient.get(
        '/dooh_api/firmware/v1/all-platforms/downloadUrl'
      );
      return response as AllPlatformFirmwareResponse;
    } catch (error) {
      console.error('Error fetching firmware data:', error);
      throw error;
    }
  }

  async fetchFirmwareBuilds(): Promise<FirmwareBuildsResponse> {
    const response = await apiClient.get<FirmwareBuilds>(
      '/dooh_api/firmware/v1/builds'
    );

    return { builds: response.data || {} };
  }
}

export const screenAPI = new ScreenAPI();
export default screenAPI;
